import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { fetchLastCoreActivityBeforeToday } from "@/services/comebackService";
import { todayInIsrael } from "@/lib/dateUtils";
import { comebackAnchor, israelDayDiff, shouldShowComebackCard } from "@/lib/comeback";

const storageKey = (userId: string) => `sy_comeback_dismissed:${userId}`;

function readDismissedAnchor(userId: string | undefined): string | null {
  if (!userId) return null;
  try {
    return localStorage.getItem(storageKey(userId));
  } catch {
    return null;
  }
}

/**
 * The "טוב שחזרת" Home card: shown when the effective user's last core
 * activity before today is seven or more Israel days old, until she dismisses
 * it for that absence. Hidden while loading and on any error.
 */
export function useComebackCard() {
  const { effectiveUserId, isImpersonating } = useImpersonation();

  const { data, isSuccess, isPending } = useQuery({
    // The Israel day is in the key so a session left open over midnight does
    // not keep showing yesterday's answer.
    queryKey: ["home", "comeback", effectiveUserId, todayInIsrael()],
    queryFn: () => fetchLastCoreActivityBeforeToday(effectiveUserId as string),
    enabled: !!effectiveUserId,
    staleTime: Infinity,
  });

  // Tagged with the user it was read for: the effective user changes when an
  // admin starts or stops viewing as a member, and one user's dismissal must
  // never hide another's card.
  const [dismissed, setDismissed] = useState(() => ({
    userId: effectiveUserId,
    anchor: readDismissedAnchor(effectiveUserId),
  }));
  useEffect(() => {
    setDismissed((prev) =>
      prev.userId === effectiveUserId
        ? prev
        : { userId: effectiveUserId, anchor: readDismissedAnchor(effectiveUserId) },
    );
  }, [effectiveUserId]);

  const last = isSuccess ? data ?? null : null;
  const now = new Date();
  const visible =
    !!effectiveUserId &&
    dismissed.userId === effectiveUserId &&
    shouldShowComebackCard({ lastActivityBeforeToday: last, now, dismissedAnchor: dismissed.anchor });
  const daysInactive = visible && last ? israelDayDiff(new Date(last), now) : 0;

  const dismiss = useCallback(() => {
    if (!last || !effectiveUserId) return;
    const anchor = comebackAnchor(last);
    setDismissed({ userId: effectiveUserId, anchor });
    // An impersonating admin hides the card for her own visit and leaves no trace.
    if (isImpersonating) return;
    try {
      localStorage.setItem(storageKey(effectiveUserId), anchor);
    } catch {
      // Storage unavailable: the in-memory dismissal still holds for this visit.
    }
  }, [last, effectiveUserId, isImpersonating]);

  // Not settled yet: Home renders neither this card nor the one it would
  // replace. A query disabled for want of a user is "pending" to React Query
  // but settled here (there is nothing to wait for).
  const pending = !!effectiveUserId && isPending;

  return { visible, daysInactive, dismiss, pending };
}
